import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { translateApiError } from '../../utils/errorMessages'
import { DEPARTMENT_TYPES } from '../../config/departmentTypes'
import DepartmentsPageHeader from './departments/DepartmentsPageHeader'
import DepartmentCreateForm from './departments/DepartmentCreateForm'
import DepartmentSection from './departments/DepartmentSection'
import { splitDepartmentsByType } from './departments/departmentListUtils'
import './Company.css'
import './departments/Departments.css'

const EMPTY_FORM = { name: '', department_type: DEPARTMENT_TYPES.PRODUCTION }

function Departments() {
  const { user } = useAuth()
  const [departments, setDepartments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState(EMPTY_FORM)

  const isManagement = user?.role === 'admin' || user?.role === 'management'
  const canReorderDepartments = user?.permissions?.['departments.update'] === true

  usePageHeader({
    title: null,
    hideTopHeader: true,
    renderActions: () => null,
  }, [])

  useEffect(() => {
    fetchDepartments()
  }, [])

  const { production, service } = useMemo(
    () => splitDepartmentsByType(departments),
    [departments],
  )

  const fetchDepartments = async () => {
    try {
      setLoading(true)
      const response = await api.get('/company/departments')
      setDepartments(response.data?.departments || [])
    } catch (error) {
      console.error('Error fetching departments:', error)
      alert('Ошибка загрузки подразделений: ' + translateApiError(error.response?.data?.error || error.message))
      setDepartments([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setFormData(EMPTY_FORM)
    setShowForm(true)
  }

  const handleCancel = () => {
    setShowForm(false)
    setFormData(EMPTY_FORM)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!formData.name.trim()) {
      alert('Название подразделения обязательно')
      return
    }

    try {
      await api.post('/company/departments', formData)
      alert('Подразделение создано')
      handleCancel()
      await fetchDepartments()
    } catch (error) {
      console.error('Error saving department:', error)
      alert('Ошибка сохранения подразделения: ' + translateApiError(error.response?.data?.error || error.message))
    }
  }

  const reorderWithin = (list, fromId, toId) => {
    const fromIndex = list.findIndex((department) => String(department.id) === String(fromId))
    const toIndex = list.findIndex((department) => String(department.id) === String(toId))
    if (fromIndex < 0 || toIndex < 0) return null

    const next = [...list]
    const [removed] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, removed)
    return next
  }

  const handleDeptDragStart = (event, department, group) => {
    event.dataTransfer.setData('application/json', JSON.stringify({ id: department.id, group }))
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleDeptDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDeptDrop = async (event, department, group) => {
    event.preventDefault()
    let payload

    try {
      payload = JSON.parse(event.dataTransfer.getData('application/json') || '{}')
    } catch {
      return
    }

    if (!payload.id || payload.group !== group) return

    const sourceList = group === 'production' ? production : service
    const reordered = reorderWithin(sourceList, payload.id, department.id)
    if (!reordered) return

    try {
      await api.patch('/company/departments/reorder', {
        production: group === 'production' ? reordered.map((item) => item.id) : production.map((item) => item.id),
        service: group === 'service' ? reordered.map((item) => item.id) : service.map((item) => item.id),
      })
      await fetchDepartments()
    } catch (error) {
      console.error('Reorder departments:', error)
      alert(`Ошибка: ${translateApiError(error.response?.data?.error || error.message)}`)
      fetchDepartments()
    }
  }

  return (
    <div className="company-page departments-list-page">
      <DepartmentsPageHeader
        departments={departments}
        canCreate={isManagement}
        onCreate={handleCreate}
      />

      {showForm && isManagement && (
        <DepartmentCreateForm
          formData={formData}
          onChange={setFormData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      )}

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : departments.length === 0 ? (
        <p className="departments-empty">Подразделения не найдены</p>
      ) : (
        <>
          <DepartmentSection
            title="Производственные подразделения"
            departments={production}
            group="production"
            canReorder={canReorderDepartments}
            onDragStart={handleDeptDragStart}
            onDragOver={handleDeptDragOver}
            onDrop={handleDeptDrop}
          />
          <DepartmentSection
            title="Непроизводственные подразделения"
            departments={service}
            group="service"
            canReorder={canReorderDepartments}
            onDragStart={handleDeptDragStart}
            onDragOver={handleDeptDragOver}
            onDrop={handleDeptDrop}
          />
        </>
      )}
    </div>
  )
}

export default Departments
